import { useState, useMemo, Fragment } from "react";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { computeNpd } from "@/utils/npdCalc";
import { formatINR } from "@/utils/formatINR";
import RoundCompareView from "@/components/RoundCompareView";

export default function ProjectCompareView({ round, currentChildren, rightLabel = "Current" }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  // Snapshot at save time vs current state. Index current children by npd_id.
  const currentByNpdId = useMemo(() => {
    const map = {};
    for (const c of (currentChildren || [])) {
      if (c && c.npd_id) map[c.npd_id] = c;
    }
    return map;
  }, [currentChildren]);

  const childRows = useMemo(() => {
    const saved = round?.snapshot?.children || [];
    return saved.map((s) => {
      const cur = currentByNpdId[s.npd_id];
      const curCalc = cur ? computeNpd(cur) : { grand_total: 0 };
      const thenTotal = Number(s.grand_total_at_save || 0);
      const nowTotal = Number(curCalc.grand_total || 0);
      const delta = nowTotal - thenTotal;
      const pct = Math.abs(thenTotal) > 0.0001 ? (delta / thenTotal) * 100 : 0;
      return {
        npd_id: s.npd_id,
        npd_number: (s.snapshot && s.snapshot.npd_number) || "(unknown)",
        part_name: (s.snapshot && s.snapshot.part_name) || "",
        part_number: (s.snapshot && s.snapshot.part_number) || "",
        thenTotal,
        nowTotal,
        delta,
        pct,
        savedSnapshot: s.snapshot,
        savedRoundLabel: s.npd_round_label,
        savedRoundId: s.npd_round_id,
        current: cur,
        missing: !cur,
      };
    });
  }, [round, currentByNpdId]);

  const heroThen = Number(round?.snapshot?.project_grand_total_at_save || 0);
  const heroNow = childRows.reduce((sum, r) => sum + r.nowTotal, 0);
  const heroDelta = heroNow - heroThen;
  const heroPct = Math.abs(heroThen) > 0.0001 ? (heroDelta / heroThen) * 100 : 0;

  const DeltaAbs = ({ d }) => {
    if (Math.abs(d) < 0.005) return <span className="text-slate-400">—</span>;
    const sign = d > 0 ? "+" : "-";
    const color = d < 0 ? "text-emerald-600" : "text-red-600";
    return <span className={color}>{sign}{formatINR(Math.abs(d))}</span>;
  };
  const DeltaPct = ({ pct }) => {
    if (Math.abs(pct) < 0.05) return <span className="text-slate-400">—</span>;
    const sign = pct > 0 ? "+" : "";
    const color = pct < 0 ? "text-emerald-600" : "text-red-600";
    return <span className={color}>{sign}{pct.toFixed(1)}%</span>;
  };

  return (
    <div className="mt-3 bg-white border border-sky-300 rounded-md overflow-hidden" data-testid="project-compare-view">
      <div className="px-3 py-2 bg-sky-50 border-b border-sky-200 text-[11px] text-slate-600 flex items-center gap-3">
        <span><strong className="text-sky-700">{round.round_label}</strong> vs {rightLabel}</span>
        <span className="text-slate-500">·</span>
        <span>Saved {new Date(round.round_date).toLocaleString("en-IN")}</span>
        {round.created_by_name && (<><span className="text-slate-500">·</span><span>by {round.created_by_name}</span></>)}
        {round.notes && (<><span className="text-slate-500">·</span><span className="italic truncate">"{round.notes}"</span></>)}
      </div>
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left p-2">Part</th>
            <th className="text-right p-2 w-28">Then ₹</th>
            <th className="text-right p-2 w-28">Now ₹</th>
            <th className="text-right p-2 w-28">Δ ₹</th>
            <th className="text-right p-2 w-20">Δ %</th>
            <th className="w-10 p-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          <tr className="bg-slate-100 border-b-2 border-slate-300">
            <td className="p-2 font-bold text-slate-900">PROJECT TOTAL</td>
            <td className="p-2 text-right font-mono font-bold text-slate-700">{formatINR(heroThen)}</td>
            <td className="p-2 text-right font-mono font-bold text-slate-900">{formatINR(heroNow)}</td>
            <td className="p-2 text-right font-mono font-bold"><DeltaAbs d={heroDelta} /></td>
            <td className="p-2 text-right font-mono font-bold"><DeltaPct pct={heroPct} /></td>
            <td></td>
          </tr>
          {childRows.length === 0 && (
            <tr><td colSpan={6} className="p-6 text-center text-slate-400 italic">No child parts in this snapshot.</td></tr>
          )}
          {childRows.map((row) => {
            const isLargeChange = Math.abs(row.pct) >= 10;
            const isExpanded = expanded[row.npd_id];
            return (
              <Fragment key={row.npd_id}>
                <tr className={isLargeChange ? "bg-amber-50/60" : "hover:bg-slate-50"}>
                  <td className="p-2">
                    <button
                      type="button"
                      onClick={() => toggle(row.npd_id)}
                      className="flex items-center gap-1 text-left"
                      disabled={row.missing}
                      data-testid={`project-compare-child-${row.npd_id}`}
                    >
                      {isExpanded ? <ChevronDown size={12} className="text-slate-400 flex-shrink-0" /> : <ChevronRight size={12} className="text-slate-400 flex-shrink-0" />}
                      <span>
                        <span className="font-mono text-[10px] text-slate-500 mr-1.5">{row.npd_number}</span>
                        <span className="text-slate-700">{row.part_name || row.part_number}</span>
                        {row.missing && <span className="ml-1 text-[10px] text-red-500 italic">deleted</span>}
                      </span>
                    </button>
                  </td>
                  <td className="p-2 text-right font-mono text-slate-600">{formatINR(row.thenTotal)}</td>
                  <td className="p-2 text-right font-mono text-slate-900">{formatINR(row.nowTotal)}</td>
                  <td className="p-2 text-right font-mono"><DeltaAbs d={row.delta} /></td>
                  <td className="p-2 text-right font-mono"><DeltaPct pct={row.pct} /></td>
                  <td className="p-2 text-center">
                    {isLargeChange && (
                      <div className="relative group inline-block">
                        <AlertTriangle size={14} className="text-amber-600 cursor-help" data-testid={`project-compare-warn-${row.npd_id}`} />
                        <div className="hidden group-hover:block absolute z-20 right-0 top-5 w-64 p-2 bg-slate-800 text-white text-[11px] rounded shadow-lg leading-snug">
                          This part moved {row.pct.toFixed(1)}% between {round.round_label} and current. Click the row to drill into the per-section breakdown and identify which inputs changed.
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
                {isExpanded && !row.missing && (
                  <tr>
                    <td colSpan={6} className="bg-slate-50/60 p-2 border-t border-slate-200">
                      <RoundCompareView
                        round={{
                          round_id: row.savedRoundId,
                          round_label: row.savedRoundLabel,
                          round_date: round.round_date,
                          notes: round.notes,
                          snapshot: row.savedSnapshot,
                        }}
                        currentNpd={row.current}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <div className="px-3 py-2 bg-slate-50 text-[10px] text-slate-500 italic border-t border-slate-200">
        Read-only. Amber warning marks parts that moved 10% or more — hover for context. Click any part to drill into the A/B/C/D/E section-by-section breakdown.
      </div>
    </div>
  );
}
